import { useState } from "react";
import { useTranslation } from "react-i18next";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from "@mui/icons-material/Close";

import {
  SearchContainer,
  SearchBar,
  SearchIconWrapper,
  StyledInputBase,
} from "./advanceSearch.styled";
import { IRowProps } from "../tables/datagrid/dataGrid.type";

interface AdvanceSearchProps {
  initialData?: any,
  setFilteredData: any,
  filterByApi?: boolean,
  clearFilteredData?: any,
  setCopyOfSearchValue?: any
  rowData?: IRowProps[];
}

export default function AdvanceSearch({ initialData, setFilteredData, filterByApi, clearFilteredData, setCopyOfSearchValue, rowData }: AdvanceSearchProps) {
  const { t } = useTranslation();
  const placeholder = t("GLOBAL.SEARCH_PLACEHOLDER_CASES");
  const [searchValue, setSearchValue] = useState('');

  /**
   * Handle searching by Object.values of 'initialData'
   * Alternative: can use an external function for processing, by providing a 'setFilteredData' function
   * 'initialData' supports objects and lists of strings
   */
  const handleSearch = () => {
    if (!searchValue.trim()) return;

    const lowerSearch = searchValue.toLowerCase();
    let matchedCasedSubstring = "";

    if (rowData && rowData.length > 0) {
      for (const row of rowData) {
        const values = Object.values(row);

        for (const val of values) {
          if (typeof val === "string") {
            const lowerVal = val.toLowerCase();
            const index = lowerVal.indexOf(lowerSearch);
            
            if (index !== -1) {
              // Extract matching substring from original value with correct casing
              matchedCasedSubstring = val.substring(index, index + searchValue.length);
              console.log("Matched substring:", matchedCasedSubstring);
              break;
            }
          }
        }

        if (matchedCasedSubstring) break;
      }
    }

    const finalSearchValue = matchedCasedSubstring || searchValue;

    if (filterByApi) {
      setFilteredData({ search: finalSearchValue });

      if (setCopyOfSearchValue) {
        setCopyOfSearchValue(finalSearchValue);
      }
    } else {
      const filteredData = (rowData ?? []).filter((row) => {
        const values = Object.values(row);
        return values.some(
          (val) =>
            typeof val === "string" &&
            val.toLowerCase().includes(lowerSearch)
        );
      });

      setFilteredData(filteredData, true, 1);
      if (setCopyOfSearchValue) {
        setCopyOfSearchValue(finalSearchValue);
      }
    }
  };

  
  /**
   * Handle button click scenarios
   * @param type a selector to tell which button we clicked
   */
  const handleEnterClick = (type) => {
    if (type === 'search') {
      // Filter the column values for data
      handleSearch()

    } else if (type === 'clear') {
      // Clear the search value in the bar
      setSearchValue('')

      // Do something upstream to swap the list of filteredValues for allValues
      // Check if clearFilteredData is provided before calling it
      if (typeof clearFilteredData === 'function') {
        clearFilteredData()
      } else if (filterByApi && typeof setFilteredData === 'function') {
        // If clearFilteredData is not provided but setFilteredData is, use it with empty search
        setFilteredData({search: ''})
      }
    }
  };

  return (
    <SearchContainer>
      <SearchBar>
        <StyledInputBase
          placeholder={placeholder}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={() => handleEnterClick('search')}
        />
      </SearchBar>
      {/* Pass the function to handle the click event */}
      <CloseIcon 
        className="mx-2" 
        onClick={() => handleEnterClick('clear')} 
        sx={{ 
          fontSize: '20px', 
          opacity: 0.6,
          cursor: 'pointer',
          '&:hover': { opacity: 1 }
        }} 
      />
      <SearchIcon 
        className="me-3" 
        onClick={() => handleEnterClick('search')} 
        sx={{ 
          fontSize: '20px', 
          opacity: 0.7,
          cursor: 'pointer',
          '&:hover': { opacity: 1 }
        }} 
      />
    </SearchContainer>
  );
}
